import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'
import 'leaflet.markercluster'

export default class extends Controller {
  static targets = ['map']

  static values = {
    currentUserId: String,
    entries: Array
  }

  connect () {
    this.loadMap()
  }

  disconnect () {
    if (this.map) this.map.remove()
  }

  loadMap () {
    const initialView = this.initialView()

    this.map = L.map(this.mapTarget, {
      attributionControl: false,
      zoomControl: false
    }).setView(initialView.center, initialView.zoom)

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19
    }).addTo(this.map)

    const markers = this.entriesValue.map((entry) => {
      const isCurrentUser = this.isCurrentUser(entry)

      return L.marker([entry.latitude, entry.longitude], {
        icon: this.markerIcon(isCurrentUser),
        isCurrentUser
      }).bindPopup(entry.popupHtml, { className: 'nature-map-popup' })
    })
    const group = L.markerClusterGroup({
      maxClusterRadius: 48,
      iconCreateFunction: (cluster) => this.clusterIcon(cluster),
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true
    })

    markers.forEach(marker => group.addLayer(marker))
    this.map.addLayer(group)

    if (this.entriesValue.length > 1) {
      const bounds = L.latLngBounds(this.entriesValue.map((entry) => [entry.latitude, entry.longitude]))
      this.map.fitBounds(bounds, {padding: [40, 40]})
    }

    window.requestAnimationFrame(() => this.map.invalidateSize())
  }

  initialView () {
    if (this.entriesValue.length === 0) {
      return { center: [39.8283, -98.5795], zoom: 6 }
    }

    const center = this.centerOfEntries()
    const maxSpread = this.maxSpreadKm(center)

    if (this.entriesValue.length === 1) {
      return { center, zoom: 13 }
    }

    if (maxSpread <= 10) return { center, zoom: 12 }
    if (maxSpread <= 50) return { center, zoom: 11 }
    if (maxSpread <= 150) return { center, zoom: 10 }

    return { center, zoom: 8 }
  }

  centerOfEntries () {
    const totals = this.entriesValue.reduce((memo, entry) => {
      memo.latitude += Number(entry.latitude)
      memo.longitude += Number(entry.longitude)
      return memo
    }, { latitude: 0, longitude: 0 })

    return [
      totals.latitude / this.entriesValue.length,
      totals.longitude / this.entriesValue.length
    ]
  }

  maxSpreadKm (center) {
    return Math.max(...this.entriesValue.map((entry) => {
      return this.distanceBetween(
        { latitude: center[0], longitude: center[1] },
        { latitude: Number(entry.latitude), longitude: Number(entry.longitude) }
      )
    }))
  }

  distanceBetween (left, right) {
    const toRadians = (value) => value * Math.PI / 180
    const earthRadiusKm = 6371
    const latitudeDelta = toRadians(right.latitude - left.latitude)
    const longitudeDelta = toRadians(right.longitude - left.longitude)
    const startLatitude = toRadians(left.latitude)
    const endLatitude = toRadians(right.latitude)

    const haversine = Math.sin(latitudeDelta / 2) ** 2 +
      Math.cos(startLatitude) * Math.cos(endLatitude) * Math.sin(longitudeDelta / 2) ** 2

    return 2 * earthRadiusKm * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  }

  markerIcon (isCurrentUser = false) {
    return L.divIcon({
      className: isCurrentUser ? 'nature-map-pin nature-map-pin--current-user' :
        'nature-map-pin nature-map-pin--other-user',
      html: isCurrentUser
        ? '<span class="nature-map-pin__marker nature-map-pin__marker--current-user"></span>'
        : '<span class="nature-map-pin__marker nature-map-pin__marker--other-user"></span>',
      iconAnchor: [14, 28],
      iconSize: [28, 28]
    })
  }

  clusterIcon (cluster) {
    const hasCurrentUser = cluster.getAllChildMarkers().some((marker) => marker.options.isCurrentUser)
    const className = hasCurrentUser
      ? 'nature-map-cluster nature-map-cluster--current-user'
      : 'nature-map-cluster nature-map-cluster--other-user'

    return L.divIcon({
      className,
      html: `<span class="nature-map-cluster__count">${cluster.getChildCount()}</span>`,
      iconAnchor: [18, 18],
      iconSize: [36, 36]
    })
  }

  isCurrentUser (entry) {
    return this.hasCurrentUserIdValue && String(entry.userId) === this.currentUserIdValue
  }
}
